'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from './ui/AppLogo';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  CreditCard,
  RefreshCw,
  AlertTriangle,
  BarChart3,
  ArrowLeftRight,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  TrendingUp,
  User,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const navGroups = [
  {
    label: 'Overview',
    items: [
      { href: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
    ],
  },
  {
    label: 'Portfolio',
    items: [
      { href: '/investors', label: 'Investors', icon: TrendingUp, badge: '8' },
      { href: '/borrowers', label: 'Borrowers', icon: Users, badge: null },
      { href: '/loans', label: 'Loans', icon: CreditCard, badge: '3' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { href: '/repayments', label: 'Repayments', icon: RefreshCw, badge: null },
      { href: '/overdue', label: 'Overdue', icon: AlertTriangle, badge: '4' },
      { href: '/transactions', label: 'Transactions', icon: ArrowLeftRight, badge: null },
    ],
  },
  {
    label: 'Insights',
    items: [
      { href: '/reports', label: 'Reports', icon: BarChart3, badge: null },
      { href: '/notifications', label: 'Notifications', icon: Bell, badge: '7' },
    ],
  },
];

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }: SidebarProps) {
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const sidebarClasses = [
    'fixed top-0 left-0 h-full z-50 flex flex-col bg-card border-r border-border sidebar-transition overflow-hidden',
    'lg:relative lg:translate-x-0',
    collapsed ? 'w-16' : 'w-60',
    mobileOpen ? 'translate-x-0 w-60' : '-translate-x-full lg:translate-x-0',
  ].join(' ');

  return (
    <aside className={sidebarClasses}>
      {/* Logo */}
      <div className={`flex items-center border-b border-border shrink-0 ${collapsed ? 'justify-center px-0 h-16' : 'gap-2 px-4 h-16'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-bold text-foreground text-base tracking-tight truncate">VatuLend</span>
          )}
        </div>
        {/* Mobile close */}
        <button
          onClick={onMobileClose}
          className="ml-auto btn-ghost p-1.5 rounded-lg lg:hidden"
          aria-label="Close sidebar"
        >
          <ChevronLeft size={16} />
        </button>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-2 mb-1 text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(item.href);
                const Icon = item.icon;
                return (
                  <li key={`nav-${item.href}`}>
                    <Link
                      href={item.href}
                      onClick={onMobileClose}
                      title={collapsed ? item.label : undefined}
                      className={[
                        'flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-150 group relative',
                        active
                          ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        collapsed ? 'justify-center' : '',
                      ].join(' ')}
                    >
                      <Icon size={18} className="shrink-0" />
                      {!collapsed && (
                        <span className="truncate flex-1">{item.label}</span>
                      )}
                      {!collapsed && item.badge && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge && (
                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-negative" />
                      )}
                      {collapsed && (
                        <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-muted text-foreground text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-border">
                          {item.label}
                          {item.badge && <span className="ml-1 text-negative">({item.badge})</span>}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom actions */}
      <div className="border-t border-border p-2 space-y-0.5 shrink-0">
        <Link
          href="/settings"
          title={collapsed ? 'Settings' : undefined}
          className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-all duration-150 group relative ${isActive('/settings') ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
        >
          <Settings size={18} className="shrink-0" />
          {!collapsed && <span>Settings</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-muted text-foreground text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-border">
              Settings
            </span>
          )}
        </Link>

        <Link
          href="/sign-up-login-screen"
          title={collapsed ? 'Sign Out' : undefined}
          className="flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-negative-subtle hover:text-negative transition-all duration-150 group relative"
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Sign Out</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-muted text-foreground text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-border">
              Sign Out
            </span>
          )}
        </Link>

        {/* User avatar — links to profile */}
        <Link
          href="/profile"
          title={collapsed ? 'My Profile' : undefined}
          className={`flex items-center gap-2 px-2 py-2 mt-1 rounded-lg transition-all duration-150 group relative ${isActive('/profile') ? 'bg-primary/10' : 'bg-muted/50 hover:bg-muted'} ${collapsed ? 'justify-center' : ''}`}
        >
          <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
            <UserCheck size={14} className="text-primary" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground truncate">Siosaia Taufa</p>
              <p className="text-[10px] text-muted-foreground truncate">Admin</p>
            </div>
          )}
          {!collapsed && (
            <User size={13} className="text-muted-foreground shrink-0" />
          )}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 rounded-md bg-muted text-foreground text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 border border-border">
              My Profile
            </span>
          )}
        </Link>
      </div>

      {/* Collapse toggle — desktop only */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}